import { useEffect, useRef, useState } from 'react';
import { useMatch, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/synapse/Icon';
import { useHasPermission } from '@/hooks/usePermissions';
import { useDebounced } from '@/hooks/useDebounced';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import type { Patient } from '@/types/patients';
import { PatientList } from '@/features/patients/PatientList';
import { PatientSearchField } from '@/features/patients/PatientSearchField';
import { useResultNavigation } from '@/features/patients/useResultNavigation';
import { QuickCreatePatient } from '@/features/patients/QuickCreatePatient';
import { usePatient } from '@/features/patients/usePatients';
import { useSelfRegistrations } from '@/features/patients/useSelfRegistrations';
import { PatientProfileCard } from '@/features/patients/PatientProfileCard';
import { EditPatientDialog } from '@/features/patients/EditPatientDialog';
import { RegistrationQrButton } from '@/features/patients/RegistrationQrButton';
import { PatientPortalLinkButton } from '@/features/patients/PatientPortalLinkButton';
import { PendingRegistrationsPane } from '@/features/patients/PendingRegistrationsPane';
import { ReviewRegistrationPane } from '@/features/patients/ReviewRegistrationPane';
import { PatientLabsCard } from '@/features/lab/PatientLabsCard';
import { QueueNumberButton } from '@/features/apt-counter/QueueNumberButton';

const NEW = 'new';

/**
 * Patient Registry — the all-roles counterpart to the doctor-only Patients page.
 * Reception/staff can search registered patients and register new ones, but the
 * clinical record is never surfaced here: only demographics are shown, and there
 * is no consultation/history access. Doctors (admins) get an "Open full record"
 * shortcut into /patients. Clinical history is hard-gated on the backend
 * (consultations routes are admin-only) — this page simply never requests it.
 */
export function PatientRegistryPage() {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // The self-registration queue lives under its own path segment rather than
  // sharing `:patientId` — matching on route rather than on a magic id value
  // means a patient can never be shadowed by a reserved word.
  const pendingListMatch = useMatch('/registry/pending');
  const reviewMatch = useMatch('/registry/pending/:submissionId');
  const submissionId = reviewMatch?.params.submissionId;
  const isPendingView = !!pendingListMatch || !!reviewMatch;

  const [query, setQuery] = useState('');
  const debounced = useDebounced(query.trim(), 200);
  const searchRef = useRef<HTMLInputElement>(null);

  const isCreating = patientId === NEW;
  const selectedId = !isCreating && !isPendingView ? patientId : undefined;

  function selectPatient(p: Patient) {
    navigate(`/registry/${p.id}`);
  }
  function openCreate(prefill: string) {
    setQuery(prefill);
    navigate('/registry/new');
  }
  function openSubmission(id: string) {
    navigate(`/registry/pending/${id}`);
  }

  const reviewPane = submissionId ? (
    // Keyed so moving between submissions remounts rather than carrying the
    // previous one's edits, photo and errors into the next one's form.
    <ReviewRegistrationPane
      key={submissionId}
      submissionId={submissionId}
      onApproved={(id) => navigate(`/registry/${id}`)}
      onDone={() => navigate('/registry/pending')}
    />
  ) : null;

  // ----- Desktop master–detail -----
  if (isDesktop) {
    return (
      <div className="mx-auto -mt-1 grid h-[calc(100vh-7rem)] w-full grid-cols-[420px_1fr] overflow-hidden rounded-lg border border-line">
        <MasterPane
          query={query}
          onQuery={setQuery}
          searchRef={searchRef}
          selectedId={selectedId}
          onSelect={selectPatient}
          onAddNew={openCreate}
          pendingActive={isPendingView}
        />
        <div className="min-w-0 overflow-y-auto bg-background p-6">
          {reviewPane ?? (
            isPendingView ? (
              <PendingRegistrationsPane onSelect={openSubmission} />
            ) : isCreating ? (
              <CreatePane initialName={query} onCreated={selectPatient} onCancel={() => navigate('/registry')} />
            ) : selectedId ? (
              <DetailPane patientId={selectedId} />
            ) : (
              <DetailEmptyPane />
            )
          )}
        </div>
      </div>
    );
  }

  // ----- Mobile -----
  if (reviewPane) {
    return (
      <div className="mx-auto w-full max-w-[560px]">
        <MobileSubHeader
          title={t('selfRegistrations.reviewTitle')}
          onBack={() => navigate('/registry/pending')}
        />
        {reviewPane}
      </div>
    );
  }

  if (isPendingView) {
    return (
      <div className="mx-auto w-full max-w-[640px]">
        <MobileSubHeader
          title={t('selfRegistrations.pendingTitle')}
          onBack={() => navigate('/registry')}
        />
        <PendingRegistrationsPane onSelect={openSubmission} />
      </div>
    );
  }

  if (isCreating) {
    return (
      <div className="mx-auto w-full max-w-[560px]">
        <MobileSubHeader title={t('patients.quickCreate.title')} onBack={() => navigate('/registry')} />
        <CreatePane initialName={query} onCreated={selectPatient} onCancel={() => navigate('/registry')} />
      </div>
    );
  }

  if (selectedId) {
    return (
      <div className="mx-auto w-full max-w-[640px]">
        <MobileSubHeader title={t('registry.title')} onBack={() => navigate('/registry')} />
        <DetailPane patientId={selectedId} />
      </div>
    );
  }

  return (
    <div className="relative mx-auto w-full max-w-[640px]">
      <header className="mb-4 flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-[-0.02em]">{t('registry.title')}</h1>
        <div className="flex items-center gap-2">
          <RegistrationQrButton iconOnly />
          <PendingRegistrationsButton onOpen={() => navigate('/registry/pending')} iconOnly />
          <Button variant="secondary" size="sm" onClick={() => navigate('/registry/new')}>
            <Icon name="plus" size={15} /> {t('patients.newPatient')}
          </Button>
        </div>
      </header>
      <MobileSearchList
        query={query}
        onQuery={setQuery}
        debounced={debounced}
        searchRef={searchRef}
        selectedId={selectedId}
        onSelect={selectPatient}
        onAddNew={openCreate}
      />
      <button
        type="button"
        aria-label={t('patients.newPatient')}
        onClick={() => navigate('/registry/new')}
        className="fixed bottom-24 right-5 z-20 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-lg"
      >
        <Icon name="plus" size={24} strokeWidth={2} />
      </button>
    </div>
  );
}

// ---------- Self-registration review queue entry point ----------

/**
 * Opens the review queue, badged with how many submissions are waiting.
 *
 * Rendered even at zero so the affordance is discoverable — a staff member who
 * has just printed the QR needs to know where submissions will appear, and a
 * control that only exists once work arrives cannot teach that.
 */
function PendingRegistrationsButton({
  onOpen,
  active,
  iconOnly,
}: {
  onOpen: () => void;
  active?: boolean;
  iconOnly?: boolean;
}) {
  const { t } = useTranslation();
  const { data } = useSelfRegistrations('pending');
  const count = data?.count ?? 0;

  return (
    <Button
      variant={active ? 'primary' : 'secondary'}
      size="sm"
      onClick={onOpen}
      {...(iconOnly ? { 'aria-label': t('selfRegistrations.pendingTitle') } : {})}
    >
      <Icon name="users" size={15} />
      {!iconOnly && t('selfRegistrations.pending')}
      {count > 0 && (
        <span
          className={cn(
            'ml-0.5 inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold',
            active ? 'bg-accent-foreground/20 text-accent-foreground' : 'bg-accent text-accent-foreground',
          )}
        >
          {count}
        </span>
      )}
    </Button>
  );
}

// ---------- Mobile search + list ----------

interface MobileSearchListProps {
  query: string;
  onQuery: (q: string) => void;
  debounced: string;
  searchRef: React.RefObject<HTMLInputElement>;
  selectedId?: string;
  onSelect: (p: Patient) => void;
  onAddNew: (prefill: string) => void;
}

function MobileSearchList({
  query,
  onQuery,
  debounced,
  searchRef,
  selectedId,
  onSelect,
  onAddNew,
}: MobileSearchListProps) {
  const { highlightedId, activeDescendantId, onKeyDown, setPatients } = useResultNavigation(onSelect);
  return (
    <div onKeyDown={onKeyDown}>
      <div className="sticky top-0 z-10 -mx-5 bg-background px-5 pb-3 pt-1">
        <PatientSearchField
          inputRef={searchRef}
          value={query}
          onChange={onQuery}
          autoFocus
          activeDescendantId={activeDescendantId}
        />
      </div>
      <PatientList
        query={debounced}
        selectedId={selectedId}
        onSelect={onSelect}
        onAddNew={onAddNew}
        variant="card"
        highlightedId={highlightedId}
        onResultsChange={setPatients}
      />
    </div>
  );
}

// ---------- Desktop master pane ----------

interface MasterPaneProps {
  query: string;
  onQuery: (q: string) => void;
  searchRef: React.RefObject<HTMLInputElement>;
  selectedId?: string;
  onSelect: (p: Patient) => void;
  onAddNew: (prefill: string) => void;
  pendingActive: boolean;
}

function MasterPane({
  query,
  onQuery,
  searchRef,
  selectedId,
  onSelect,
  onAddNew,
  pendingActive,
}: MasterPaneProps) {
  const navigate = useNavigate();
  const debounced = useDebounced(query.trim(), 200);
  const { highlightedId, activeDescendantId, onKeyDown, setPatients } = useResultNavigation(onSelect);
  return (
    <div className="flex min-h-0 flex-col border-r border-line bg-paper" onKeyDown={onKeyDown}>
      {/* The desktop layout has no page header, so the registration-QR and
          review-queue actions live above the search field. */}
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-3">
        <RegistrationQrButton />
        <div className="flex-1" />
        <PendingRegistrationsButton
          onOpen={() => navigate('/registry/pending')}
          active={pendingActive}
        />
      </div>
      <div className="shrink-0 p-4">
        <PatientSearchField
          inputRef={searchRef}
          value={query}
          onChange={onQuery}
          autoFocus
          size="md"
          activeDescendantId={activeDescendantId}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
        <PatientList
          query={debounced}
          selectedId={selectedId}
          onSelect={onSelect}
          onAddNew={onAddNew}
          variant="row"
          highlightedId={highlightedId}
          onResultsChange={setPatients}
        />
      </div>
    </div>
  );
}

// ---------- Detail (demographics only — no clinical history) ----------

function DetailPane({ patientId }: { patientId: string }) {
  const { t } = useTranslation();
  // The branch is "can you follow this through to the full clinical record?",
  // which is the consultations key — not an administrative one.
  const canOpenRecord = useHasPermission('clinical.consultations.read');
  const navigate = useNavigate();
  const { data: patient } = usePatient(patientId);
  const [editing, setEditing] = useState(false);

  if (!patient) {
    return <div className="py-10 text-center text-[13px] text-muted">{t('common.loading')}</div>;
  }

  return (
    <div className="flex flex-col gap-6">
      <PatientProfileCard patient={patient} />

      {/* Demographics are editable here (registration details); the clinical
          record stays behind the doctor-only Patients page. */}
      <div className="flex flex-wrap items-start gap-2">
        {/* First, because it is the reason most patients are opened here: they
            are standing at the counter and need a number to wait with. */}
        <QueueNumberButton patientId={patientId} />
        <Button variant="secondary" onClick={() => setEditing(true)}>
          <Icon name="edit" size={16} /> {t('patients.editDetails')}
        </Button>
        {canOpenRecord && (
          <Button variant="secondary" onClick={() => navigate(`/patients/${patientId}`)}>
            <Icon name="arrow_up_right" size={16} /> {t('registry.openFullRecord')}
          </Button>
        )}
        {/* Reception is where a patient is standing at the counter asking how
            they will get their results, so the link belongs on this pane and
            not only on the doctor's chart. */}
        <PatientPortalLinkButton patient={patient} />
      </div>

      {!canOpenRecord && (
        <p className="rounded-md border border-line bg-paper-2 px-3.5 py-3 text-[12.5px] text-muted">
          {t('registry.demographicsOnly')}
        </p>
      )}

      {/* Read-only: the registry is a demographics surface, and raising an
          order belongs on the lab desk or the clinical record. The history
          itself shows because "has this patient's blood been taken?" is a
          reception question, and `lab.read` already governs the answer. */}
      <PatientLabsCard patientId={patientId} readOnly />

      {editing && <EditPatientDialog patient={patient} onClose={() => setEditing(false)} />}
    </div>
  );
}

function CreatePane({
  initialName,
  onCreated,
  onCancel,
}: {
  initialName: string;
  onCreated: (p: Patient) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-xl font-semibold tracking-[-0.02em]">{t('patients.quickCreate.title')}</h2>
        <p className="mt-1 text-[13px] text-muted">{t('patients.quickCreate.subtitle')}</p>
      </div>
      <QuickCreatePatient initialName={initialName} onCreated={onCreated} onCancel={onCancel} />
    </div>
  );
}

function DetailEmptyPane() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col items-center justify-center px-10 text-center">
      <div className="mb-5 flex h-[76px] w-[76px] items-center justify-center rounded-full border border-line bg-paper-2 text-muted-2">
        <Icon name="users" size={32} strokeWidth={1.4} />
      </div>
      <div className="text-lg font-semibold tracking-[-0.02em]">{t('registry.selectTitle')}</div>
      <p className="mt-2 max-w-[320px] text-[13.5px] leading-relaxed text-muted">{t('registry.selectBody')}</p>
    </div>
  );
}

function MobileSubHeader({ title, onBack }: { title: string; onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mb-4 flex items-center gap-2">
      <button
        type="button"
        onClick={onBack}
        aria-label={t('patients.backToPatients')}
        className="flex h-9 w-9 items-center justify-center rounded-md text-ink hover:bg-paper-2"
      >
        <Icon name="back" size={18} />
      </button>
      <h1 className="min-w-0 flex-1 truncate text-lg font-semibold tracking-[-0.02em]">{title}</h1>
    </div>
  );
}
